/**
 * CompositeVersionItem — read-only display for a single composite pricing version.
 *
 * Shows resolved prices in a 3×3 table (monthly/quarterly/yearly × netto/VAT/brutto),
 * validity range, AKTYWNA/SEED badges, condition config (formatted), and
 * a collapsible "Struktura" section with child component details.
 *
 * Read-only: no mutation buttons (AC-8).
 * Error state: shows bg-red-50 error box when priceResolutionError set (AC-9).
 * Currency: uses formatCurrency with currencyDisplay: "code" — "PLN" not "zł" (AC-5).
 *
 * @see AC-4, AC-5, AC-8, AC-9 in docs/specifications/2026-06-10-admin-pricing-composite-view/spec.md
 */
import React, { useState } from "react";
import type {
  CompositeVersionView,
  ResolvedPriceBreakdown,
} from "../../../../types/adminBilling";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { formatConditionConfig } from "./formatConditionConfig";

export interface CompositeVersionItemProps {
  version: CompositeVersionView;
  /** Whether the version is currently active (validFrom ≤ now < validTo). */
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (date?: string | null): string => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("pl-PL");
};

type PriceRow = "netto" | "vat" | "brutto";

const INTERVALS = ["monthly", "quarterly", "yearly"] as const;
const INTERVAL_LABELS: Record<string, string> = {
  monthly: "Miesięczny",
  quarterly: "Kwartalny",
  yearly: "Roczny",
};

const ROW_KEYS: PriceRow[] = ["netto", "vat", "brutto"];
const ROW_LABELS: Record<PriceRow, string> = {
  netto: "Netto",
  vat: "VAT",
  brutto: "Brutto",
};

/** Format a grosze value as PLN using project-standard currencyDisplay: "code". */
function formatGrosze(cents: number | undefined, currency: string): string {
  if (cents === undefined || cents === null) return "—";
  return formatCurrency(cents / 100, currency);
}

/** Extract numeric value for a row key from a price breakdown. */
function getPriceValue(price: ResolvedPriceBreakdown, row: PriceRow): number {
  return price[row];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CompositeVersionItem: React.FC<CompositeVersionItemProps> = ({
  version,
  isActive,
}) => {
  const [structureOpen, setStructureOpen] = useState(false);
  const structureId = `structure-${version.id}`;

  const hasError =
    !!version.priceResolutionError || version.resolvedPrices === null;

  // Build interval → price map for table lookup
  const priceByInterval: Partial<Record<string, ResolvedPriceBreakdown>> = {};
  if (version.resolvedPrices) {
    version.resolvedPrices.forEach((p) => {
      priceByInterval[p.interval] = p;
    });
  }

  // Currency from first resolved price (falls back to PLN)
  const currency = version.resolvedPrices?.[0]?.currency ?? "PLN";

  const conditionText = formatConditionConfig(version.conditionConfig);

  return (
    <div
      className={`border rounded-md p-4 ${
        isActive
          ? "bg-green-50 border-green-200"
          : "bg-neutral-50 border-neutral-200"
      }`}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Header: badges + validity range                                     */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {isActive && (
            <span className="text-xs bg-green-200 text-green-800 font-medium px-2 py-0.5 rounded">
              AKTYWNA
            </span>
          )}
          {version.isManagedBySeed && (
            <span
              className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
              title="Zarządzane przez seed runner — tylko odczyt"
            >
              SEED
            </span>
          )}
        </div>
        <span className="text-xs text-neutral-500">
          {version.validTo
            ? `${formatDate(version.validFrom)} — ${formatDate(version.validTo)}`
            : `od ${formatDate(version.validFrom)}`}
        </span>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Condition config (formatted PL text)                                */}
      {/* ------------------------------------------------------------------ */}
      {conditionText && (
        <div className="text-xs text-neutral-600 mb-3">
          <span className="font-medium">Warunek:</span>{" "}
          <span>{conditionText}</span>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Price table OR error state (AC-9)                                   */}
      {/* ------------------------------------------------------------------ */}
      {hasError ? (
        <div className="rounded border bg-red-50 border-red-200 p-3 text-sm text-red-700">
          Nie udało się wyliczyć ceny dla tej wersji
          {version.priceResolutionError && (
            <span className="block text-xs mt-1 text-red-500">
              {version.priceResolutionError}
            </span>
          )}
        </div>
      ) : (
        /* Price table: 3 columns (monthly/quarterly/yearly) × 3 rows (netto/VAT/brutto) */
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="text-left py-1.5 pr-4 text-neutral-500 font-medium w-16" />
                {INTERVALS.map((interval) => (
                  <th
                    key={interval}
                    className="text-right py-1.5 px-3 text-neutral-500 font-medium"
                  >
                    {INTERVAL_LABELS[interval]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROW_KEYS.map((row) => (
                <tr key={row} className="border-t border-neutral-100">
                  <td className="py-1.5 pr-4 text-neutral-600 font-medium">
                    {ROW_LABELS[row]}
                  </td>
                  {INTERVALS.map((interval) => {
                    const price = priceByInterval[interval];
                    const value = price ? getPriceValue(price, row) : undefined;
                    return (
                      <td
                        key={interval}
                        className="text-right py-1.5 px-3 text-neutral-800 tabular-nums"
                      >
                        {formatGrosze(value, currency)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Collapsible "Struktura" section (AC-4: zwinięta domyślnie)          */}
      {/* ------------------------------------------------------------------ */}
      <div className="mt-3 pt-3 border-t border-neutral-200">
        <button
          type="button"
          onClick={() => setStructureOpen((o) => !o)}
          aria-expanded={structureOpen}
          aria-controls={structureId}
          className="text-xs text-neutral-600 hover:text-neutral-900 font-medium flex items-center gap-1"
        >
          Struktura <span aria-hidden="true">{structureOpen ? "▲" : "▼"}</span>
        </button>

        <div
          id={structureId}
          hidden={!structureOpen}
          className="mt-2 space-y-2"
        >
          {version.children.length === 0 ? (
            <p className="text-xs text-neutral-400 italic">Brak dzieci</p>
          ) : (
            version.children.map((child, idx) => (
              <div
                key={`${child.name}-${idx}`}
                className="bg-white border border-neutral-200 rounded p-2 text-xs"
              >
                <div className="font-medium text-neutral-800">{child.name}</div>
                <div className="text-neutral-500 mt-0.5">
                  {child.componentType} · {child.calculatorType}
                </div>
                <div className="font-mono text-neutral-600 mt-1 break-all">
                  {JSON.stringify(child.config)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
